import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import BanIcon from '@/public/icons/approval/ban.svg';
import CheckIcon from '@/public/icons/approval/check.svg';
import EyeIcon from '@/public/icons/approval/eye.svg';
import DeactivatedBadge from '@/components/Approval/Common/DeactivatedBadge';
import OnLeaveBadge from '@/components/Approval/Common/OnLeaveBadge';
import { resolveDepartmentName } from '@/utils/approval/ceoAffiliation';
import { formatCardDate, normalizeLang, pickLang } from './statusMapping';
import {
  ApprovalLineCardHeader,
  ApprovalLineCardHeaderDept,
  ApprovalLineCardHeaderPosition,
  ApprovalLineCardName,
  ApprovalLineCardOrderChip,
  ApprovalLineCardRoot,
  ApprovalLineCardStatus,
  ApprovalLineCardStatusChip,
} from './styles';
import type { ApprovalViewApprover, DocumentStatus } from './types';
import type { StatusBadgeTone } from './statusMapping';

/**
 * PHP iframe 톤 결재라인 카드 (권위: reference_legacy_approval_view_styles.md).
 * isCurrent 단독 info 강제는 반려/보류 문서 파랑 오표시 버그 — documentStatus 를 함께 본다.
 */

type ApprovalLineCardProps = {
  approver: ApprovalViewApprover;
  isCurrent?: boolean;
  /** 문서 status. 카드 보더/딱지 톤 분기에 필요. 미전달 시 강조 없음(회색)으로 처리 */
  documentStatus?: DocumentStatus;
  /** 카드 우상단 [순번] chip 에 노출. ApprovalLineSlot 에서 그룹 인덱스 기반으로 계산해 전달 */
  orderLabel?: string | number;
  /** 협조결재 카드 여부. 기본 대기 상태 텍스트가 '결재' → '협조' */
  isCooperation?: boolean;
  /**
   * 결재 전이지만 문서를 본 적 있는지(레거시 PHP VIEW_DATE) — 대기 카드 footer 눈 아이콘.
   * 작성자 본인은 의미 없어 caller 가 set 에서 제외해 false 로 전달.
   */
  hasViewed?: boolean;
  /** 퇴사자 여부 — 이름 옆 '퇴사' 배지 */
  isDeactivated?: boolean;
  /** 휴직자 여부 — 이름 옆 '휴직' 배지. 퇴사와 겹치면 퇴사만 표시한다 */
  isOnLeave?: boolean;
};

const ApprovalLineCard = ({
  approver,
  isCurrent,
  documentStatus,
  orderLabel,
  isCooperation = false,
  hasViewed = false,
  isDeactivated = false,
  isOnLeave = false,
}: ApprovalLineCardProps) => {
  const { t, i18n } = useTranslation();
  const lang = useMemo(() => normalizeLang(i18n.language), [i18n.language]);

  const name = pickLang(approver.name, lang);
  const position = pickLang(approver.positionName, lang);
  const department =
    resolveDepartmentName(approver, approver.departmentName) ?? '';

  const isCompleted = approver.status === 'C';

  // 순번 없는 협조결재는 멤버 전원이 isCurrent — 보류/반려를 documentStatus 로 폴백하면
  // 누르지 않은 멤버까지 번짐.
  const canInheritDocumentStatus = Boolean(isCurrent) && !isCooperation;

  // 카드 보더 + 순번 chip 강조 톤. 진행중/보류 문서의 currentApprover 만 강조, 반려자는 하단 chip 만 빨강.
  const cardHighlightTone: StatusBadgeTone | undefined = (() => {
    if (isCooperation && approver.status === 'H') return 'warning';
    if (!isCurrent) return undefined;
    if (documentStatus === 'P') return 'info';
    if (documentStatus === 'H')
      return canInheritDocumentStatus ? 'warning' : undefined;
    return undefined;
  })();

  // 하단 chip: approver.status 1차 권위 + 개별 status 미갱신('0' 유지) 케이스 커버로 documentStatus 병행.
  // P 폴백은 협조도 유지 — 순번 없으면 멤버 전원이 실제로 결재 가능한 대기 상태.
  const { chipText, chipTone } = ((): {
    chipText: string;
    chipTone: StatusBadgeTone;
  } => {
    if (
      approver.status === 'X' ||
      (canInheritDocumentStatus && documentStatus === 'X')
    ) {
      return { chipText: t('취소', { context: 'status' }), chipTone: 'danger' };
    }
    if (
      approver.status === 'R' ||
      (canInheritDocumentStatus && documentStatus === 'R')
    ) {
      return { chipText: t('반려', { context: 'status' }), chipTone: 'danger' };
    }
    if (
      approver.status === 'H' ||
      (canInheritDocumentStatus && documentStatus === 'H')
    ) {
      return {
        chipText: t('결재 보류중', { context: 'status' }),
        chipTone: 'warning',
      };
    }
    if (approver.status === 'P' || (isCurrent && documentStatus === 'P')) {
      return {
        chipText: t('결재 대기중', { context: 'status' }),
        chipTone: 'info',
      };
    }
    return {
      chipText: isCooperation
        ? t('협조', { context: 'role' })
        : t('결재', { context: 'role' }),
      chipTone: 'neutral',
    };
  })();

  // 대기 카드가 본 적 있으면(neutral + hasViewed) 눈 아이콘 prepend — PHP `$see` 로직.
  const statusBarContent = isCompleted ? (
    <>
      <CheckIcon
        width={12}
        height={12}
        style={{ marginRight: 4, color: 'var(--dk-text-strong, #555)' }}
      />
      {formatCardDate(approver.approvedAt) ?? t('완료')}
    </>
  ) : chipTone === 'danger' ? (
    <>
      <BanIcon width={12} height={12} style={{ marginRight: 4 }} />
      {chipText}
    </>
  ) : chipTone === 'neutral' && hasViewed ? (
    <>
      <EyeIcon width={14} height={14} style={{ marginRight: 4 }} />
      {chipText}
    </>
  ) : (
    chipText
  );

  // [순번] chip 라벨. 미전달 시 approver.order 그대로.
  const orderText =
    orderLabel !== undefined && orderLabel !== ''
      ? String(orderLabel)
      : String(approver.order);

  return (
    <ApprovalLineCardRoot currentTone={cardHighlightTone}>
      <ApprovalLineCardOrderChip currentTone={cardHighlightTone}>
        {orderText}
      </ApprovalLineCardOrderChip>
      {department && (
        <ApprovalLineCardHeader>
          <ApprovalLineCardHeaderDept>{department}</ApprovalLineCardHeaderDept>
        </ApprovalLineCardHeader>
      )}
      {position && (
        <ApprovalLineCardHeaderPosition>
          {position}
        </ApprovalLineCardHeaderPosition>
      )}
      <ApprovalLineCardName>
        {isDeactivated && <DeactivatedBadge />}
        {!isDeactivated && isOnLeave && <OnLeaveBadge />}
        {name}
      </ApprovalLineCardName>
      <ApprovalLineCardStatus>
        <ApprovalLineCardStatusChip tone={chipTone} isCompleted={isCompleted}>
          {statusBarContent}
        </ApprovalLineCardStatusChip>
      </ApprovalLineCardStatus>
    </ApprovalLineCardRoot>
  );
};

export default ApprovalLineCard;
